import React, { useCallback, useState } from 'react';
import { graphql, usePreloadedQuery, type EntryPointProps, type PreloadedQuery } from 'react-relay';
import { Box } from '@atlaskit/primitives';
import { cssMap, cx } from '@atlaskit/css';
import { BulkOpsCoreWithToolbar } from '@atlassian/jira-bulk-operations-async-core/src/index.tsx';
import type { BulkOpsCoreType } from '@atlassian/jira-bulk-operations-async-core/src/ui/index.tsx';
import { useReferenceLoader } from '@atlassian/jira-bulk-operations-common/src/utils/reference-loader/use-reference-loader/index.tsx';
import type { AnyEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import type { EntryPointReferenceSubject } from '@atlassian/jira-entry-point/src/controllers/utils/use-entry-point-load-manager/index.tsx';
import Resizer from '@atlassian/jira-flex-resizer/src/ui/main.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { EntryPointContentQuery } from './__generated__/EntryPointContentQuery.graphql';
import { getStoredRatio, setStoredRatio } from './utils.tsx';

const SidebarForm = ({
	resizerStyles,
	setResizerRef,
	setFormRef,
}: {
	resizerStyles: { [key: string]: unknown };
	setResizerRef: (node: HTMLElement | null) => void;
	setFormRef: (node: HTMLElement | null) => void;
}) => {
	const setRef = useCallback(
		(node: HTMLElement | null) => {
			setFormRef(node);
			setResizerRef(node);
		},
		[setFormRef, setResizerRef],
	);

	return (
		<Box
			testId="bulk-operations-full-core.box"
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop
			style={resizerStyles}
			ref={setRef}
			padding="space.150"
			xcss={cx(
				styles.root,
				expVal('sunsetting_bulk_edit', 'isEnabled', false) && styles.overflowFix,
			)}
		/>
	);
};

type BulkOperationsCoreLoader<TEntryPoint extends AnyEntryPoint | undefined = undefined> = Omit<
	BulkOpsCoreType<TEntryPoint>,
	'bulkOperationsForm' | 'onOpenForm' | 'onCloseForm'
> & {
	bulkOpsCoreEntryPointReference: // eslint-disable-next-line @typescript-eslint/no-explicit-any
	EntryPointReferenceSubject<any>;
};

export type BulkOperationsCoreFullProps<TEntryPoint extends AnyEntryPoint | undefined = undefined> =
	Omit<BulkOperationsCoreLoader<TEntryPoint>, 'hasBulkEditPermission'> & {
		permissionsQueryRef: PreloadedQuery<EntryPointContentQuery>;
	};

const DEFAULT_RATIO = 0.35;
const MIN_RATIO = 0.3;
const MAX_RATIO = 0.5;

export const useSidebar = () => useState(false);

const BulkOperationsCoreFullInternal = <TEntryPoint extends AnyEntryPoint | undefined = undefined>(
	props: BulkOperationsCoreLoader<TEntryPoint>,
) => {
	const [openSidebar, setOpenSidebar] = useSidebar();
	const bulkOperationsForm = useReferenceLoader();

	const onOpenForm = useCallback(() => {
		setOpenSidebar(true);
	}, [setOpenSidebar]);

	const onCloseForm = useCallback(() => {
		setOpenSidebar(false);
	}, [setOpenSidebar]);

	const onRatioChange = useCallback((ratio: number) => {
		setStoredRatio(ratio);
	}, []);

	return (
		<>
			<BulkOpsCoreWithToolbar<TEntryPoint>
				{...props}
				bulkOperationsForm={bulkOperationsForm}
				onCloseForm={onCloseForm}
				onOpenForm={onOpenForm}
			/>
			{openSidebar && (
				<Resizer
					defaultRatio={getStoredRatio(DEFAULT_RATIO)}
					minRatio={MIN_RATIO}
					maxRatio={MAX_RATIO}
					onRatioChange={onRatioChange}
				>
					{({ ref, style }) => (
						<SidebarForm
							resizerStyles={style}
							setResizerRef={ref}
							setFormRef={bulkOperationsForm.setRef}
						/>
					)}
				</Resizer>
			)}
		</>
	);
};

export const BulkOperationsCoreFull = <TEntryPoint extends AnyEntryPoint | undefined = undefined>({
	permissionsQueryRef,
	...props
}: BulkOperationsCoreFullProps<TEntryPoint>): React.JSX.Element => {
	const data = usePreloadedQuery<EntryPointContentQuery>(
		graphql`
			query EntryPointContentQuery($cloudId: ID!) @preloadable {
				jira {
					permission(cloudId: $cloudId, type: BULK_CHANGE) {
						hasPermission
					}
				}
			}
		`,
		permissionsQueryRef,
	);

	return (
		<BulkOperationsCoreFullInternal<TEntryPoint>
			{...props}
			hasBulkEditPermission={data.jira?.permission?.hasPermission ?? false}
		/>
	);
};

export type EntryPointContentProps = EntryPointProps<
	{
		permissionsQuery: EntryPointContentQuery;
	},
	{},
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	any,
	{}
>;

const EntryPointContent = ({
	props,
	queries: { permissionsQuery },
}: EntryPointContentProps): React.JSX.Element => (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	<BulkOperationsCoreFull<any> {...props} permissionsQueryRef={permissionsQuery} />
);

export default EntryPointContent;

const styles = cssMap({
	root: {
		height: '100%',
	},
	overflowFix: {
		overflow: 'hidden',
		position: 'relative',
	},
});
